/**
 * 「模型查询 / 版本查询」共用的纯函数（无依赖，便于 node --test）。
 * 设计见 openspec/changes/model-management-revamp/design.md §4、§6.2、§10。
 * 类型只声明本文件用到的字段，与 apis/model.ts 结构兼容。
 */

export interface ListStat {
  name: string;
  category: string | null;
  type: string | null;
  current_version: string;
  tags: string[];
  created: string;
  updated: string;
  active_versions: number;
  total_versions: number;
  all_inactive: boolean;
  frameworks?: string[];
  last_uploaded?: string | null;
}

export interface NameReg {
  name: string;
  category: string;
  type: string | null;
  install_dir: string;
  description: string | null;
  status: 'enabled' | 'disabled';
  runtime_module: string | null;
  latest_version: string | null;
  active_versions: number;
}

/** 合并后的列表行：版本统计（可能没有版本）+ 登记信息（可能未登记）。 */
export interface MergedModelRow {
  name: string;
  category: string | null;
  type: string | null;
  tags: string[];
  created: string | null;
  updated: string | null;
  active_versions: number;
  total_versions: number;
  all_inactive: boolean;
  latest_version: string | null;
  frameworks: string[];
  /** 最近一次上传时间（停用 / 启用不影响） */
  last_uploaded: string | null;
  reg: NameReg | null;
}

/**
 * 合并 listModels（版本统计）与 listModelNames（登记信息）。
 * - 分类以登记信息为准（model 表的 category/type 只读兼容，可能与登记不一致）；
 * - 只登记、还没上传版本的名称也列出（版本数 0）；
 * - 按名称排序，保证列表稳定。
 */
export function mergeModelRows(stats: ListStat[], regs: NameReg[]): MergedModelRow[] {
  const regMap = new Map(regs.map((r) => [r.name, r]));
  const out: MergedModelRow[] = stats.map((s) => {
    const reg = regMap.get(s.name) ?? null;
    return {
      name: s.name,
      category: reg ? reg.category : s.category,
      type: reg ? reg.type : s.type,
      tags: s.tags,
      created: s.created,
      updated: s.updated,
      active_versions: s.active_versions,
      total_versions: s.total_versions,
      all_inactive: s.all_inactive,
      latest_version: s.current_version || reg?.latest_version || null,
      frameworks: s.frameworks ?? [],
      last_uploaded: s.last_uploaded ?? s.updated,
      reg,
    };
  });
  const seen = new Set(stats.map((s) => s.name));
  for (const r of regs) {
    // 只登记、还没有版本的名称要能看到（刚登记待上传）；已停用且没有可用版本的不再列出
    if (seen.has(r.name) || (r.status === 'disabled' && r.active_versions === 0)) {
      continue;
    }
    out.push({
      name: r.name,
      category: r.category,
      type: r.type,
      tags: [],
      created: null,
      updated: null,
      active_versions: r.active_versions,
      total_versions: 0,
      all_inactive: false,
      latest_version: r.latest_version,
      frameworks: [],
      last_uploaded: null,
      reg: r,
    });
  }
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

/** 仿真模块显示名。 */
export const RUNTIME_MODULE_LABEL: Record<string, string> = {
  Prediction: '预测',
  PointPillarDetection: '激光检测',
  TrafficLightDetector: '红绿灯',
};

/** 文件名格式（扩展名，小写）；没有扩展名为空串。 */
export function fileFormat(fileName: string): string {
  const i = fileName.lastIndexOf('.');
  return i > 0 ? fileName.slice(i + 1).toLowerCase() : '';
}

/** 一级分类显示名：有中文 label 用 label，否则原值；空为「—」。 */
/** 中台模型类别（二级分类的中文名，与中台页面上的模型类型名一致）；没配或没有二级分类为 null。 */
export function ndpTypeLabel(
  cats: Record<string, { type_labels?: Record<string, string> }> | null | undefined,
  type: string | null | undefined,
): string | null {
  if (!type) {
    return null;
  }
  for (const c of Object.values(cats ?? {})) {
    const label = c.type_labels?.[type];
    if (label) {
      return label;
    }
  }
  return null;
}

/** 二级分类下拉选项展示：`LANE（视觉车道线检测）`；没配中文名只显示代码，空显示 —。 */
export function typeLabelText(
  cats: Record<string, { type_labels?: Record<string, string> }> | null | undefined,
  type: string | null | undefined,
): string {
  if (!type) {
    return '—';
  }
  for (const c of Object.values(cats ?? {})) {
    const label = c.type_labels?.[type];
    if (label) {
      return `${type}（${label}）`;
    }
  }
  return type;
}

export function categoryLabel(
  cats: Record<string, { label: string; types: string[] }> | null | undefined,
  key: string | null | undefined,
): string {
  if (!key) {
    return '—';
  }
  return cats?.[key]?.label ?? key;
}

export interface ChangeLike {
  op: 'replace' | 'add' | 'remove';
  name: string;
  from: string | null;
  to: string | null;
}

export const CHANGE_OP_LABEL: Record<ChangeLike['op'], string> = {
  replace: '替换',
  add: '新增',
  remove: '移除',
};

/** 改动的版本描述：replace「from → to」，add「to」，remove「from」。 */
export function changeVersionText(c: ChangeLike): string {
  if (c.op === 'replace') {
    return `${c.from ?? '—'} → ${c.to ?? '—'}`;
  }
  if (c.op === 'add') {
    return c.to ?? '—';
  }
  return c.from ?? '—';
}

/** 改动按 op 统计，用于标题摘要，如「替换 2 · 新增 1」；无改动返回「无改动」。 */
export function changeSummary(changes: ChangeLike[]): string {
  const n: Record<string, number> = { replace: 0, add: 0, remove: 0 };
  for (const c of changes) {
    n[c.op] = (n[c.op] ?? 0) + 1;
  }
  const parts = (['replace', 'add', 'remove'] as const)
    .filter((op) => n[op] > 0)
    .map((op) => `${CHANGE_OP_LABEL[op]} ${n[op]}`);
  return parts.length ? parts.join(' · ') : '无改动';
}

/** 未登记名称的目录冲突提示；无冲突返回空数组。 */
export function unregisteredConflicts(u: {
  dir_taken_by: string | null;
  dir_claimed_by: string[];
}): string[] {
  const out: string[] = [];
  if (u.dir_taken_by) {
    out.push(`建议目录（同二级分类）已属于已登记模型 ${u.dir_taken_by}`);
  }
  if (u.dir_claimed_by && u.dir_claimed_by.length > 0) {
    out.push(`未登记的 ${u.dir_claimed_by.join('、')} 也推断出同一目录、同一二级分类`);
  }
  return out;
}

/** 从 URL search 读取 ?name=，没有返回 null。 */
export function releaseNameFromSearch(search: string): string | null {
  const v = new URLSearchParams(search).get('name');
  return v && v.trim() ? v.trim() : null;
}


// ============================================================
// 主版本列表筛选（数据来自 GET /model/release/tree，全部字段都在前端过滤）
// ============================================================

/** 创建类型：trigger_type → 显示名。 */
export const RELEASE_TRIGGER_LABEL: Record<string, string> = {
  manual: '手动创建',
  ndp_sync: '中台同步',
  upload: '上传生成（旧）',
};

export const RELEASE_KIND_LABEL: Record<string, string> = { release: '正式', dev: '测试' };

export interface ReleaseRowLike {
  name: string;
  category: string | null;
  kind: string;
  major_group: string;
  based_on: string | null;
  trigger_type: string;
  description: string | null;
  created_by: string;
  create_time: string;
}

export interface ReleaseFilter {
  /** 关键字：匹配版本号、描述、基线版本 */
  q?: string;
  majorGroup?: string;
  kind?: string;
  category?: string;
  trigger?: string;
  creator?: string;
  /** 创建日期范围（含两端，YYYY-MM-DD） */
  dateFrom?: string;
  dateTo?: string;
}

export function filterReleases<T extends ReleaseRowLike>(rows: T[], f: ReleaseFilter): T[] {
  const kw = (f.q ?? '').trim().toLowerCase();
  return rows.filter((r) => {
    if (f.majorGroup && r.major_group !== f.majorGroup) return false;
    if (f.kind && r.kind !== f.kind) return false;
    if (f.category && r.category !== f.category) return false;
    if (f.trigger && r.trigger_type !== f.trigger) return false;
    if (f.creator && r.created_by !== f.creator) return false;
    const day = r.create_time.slice(0, 10);
    if (f.dateFrom && day < f.dateFrom) return false;
    if (f.dateTo && day > f.dateTo) return false;
    if (kw) {
      const hay = `${r.name} ${r.description ?? ''} ${r.based_on ?? ''}`.toLowerCase();
      if (!hay.includes(kw)) return false;
    }
    return true;
  });
}

/** 「允许上传的模型类别」前端校验（与后端一致）：非空、名字合规、不重复。返回错误文案或 null。 */
export function uploadNamesError(names: string[]): string | null {
  if (names.length === 0) return '至少保留一个模型类别';
  const seen = new Set<string>();
  for (const n of names) {
    if (!/^[A-Za-z0-9_\-\u4e00-\u9fff]{1,64}$/.test(n)) return `模型类别名不合法：${n}`;
    if (seen.has(n)) return `模型类别重复：${n}`;
    seen.add(n);
  }
  return null;
}

// ============================================================
// 小模型：一个模型版本里的文件按「小模型」归组（名称 + tag）
// ============================================================

export interface SubModel {
  /** 小模型名称：engine 去掉 `_GPU_Trt<版本>_<精度>` / `_DLA...` 尾巴，其它文件去掉扩展名 */
  name: string;
  /** tag = 文件后缀（格式），每个 tag 带对应的完整文件名 */
  tags: Array<{ tag: string; file: string }>;
}

// 算法 engine 命名：<onnx 名>_GPU_Trt<版本>_<精度>.engine（另有 DLA 变体）
const ENGINE_SUFFIX_RE = /_(GPU|DLA)[A-Za-z0-9]*_Trt[0-9][0-9.]*(_[A-Za-z0-9]+)?$/i;

/** 文件名 → 小模型名称。 */
export function subModelName(fileName: string): string {
  const ext = fileFormat(fileName);
  const stem = ext ? fileName.slice(0, fileName.length - ext.length - 1) : fileName;
  if (ext === 'engine' || ext === 'plan') {
    return stem.replace(ENGINE_SUFFIX_RE, '') || stem;
  }
  return stem;
}

/** 按小模型归组；按名称排序，同一小模型内 tag 按 onnx → engine/plan → 其它 排序。 */
export function groupSubModels(fileNames: string[]): SubModel[] {
  const order = (t: string) => (t === 'onnx' ? 0 : t === 'engine' || t === 'plan' ? 1 : 2);
  const groups = new Map<string, SubModel>();
  for (const file of Array.from(new Set(fileNames)).sort()) {
    const name = subModelName(file);
    const g = groups.get(name) ?? { name, tags: [] };
    g.tags.push({ tag: fileFormat(file) || '无后缀', file });
    groups.set(name, g);
  }
  return Array.from(groups.values())
    .map((g) => ({ ...g, tags: g.tags.sort((a, b) => order(a.tag) - order(b.tag) || a.file.localeCompare(b.file)) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}


// ============================================================
// 模型查询页：版本行构造 + 版本筛选（版本号 / 上传人 / 上传时间 / 状态 / 来源）
// ============================================================

export interface VersionFileLike {
  file_name: string;
  tag: string;
  active: boolean;
  create_time: string;
  uploader: string;
  meta: Record<string, unknown> | null;
  description?: string | null;
}

export interface VersionHistoryLike {
  version: string;
  active: boolean;
  latest_time: string;
  tags: VersionFileLike[];
}

export type VersionSource = 'upload' | 'ndp';

export interface VersionRow {
  name: string;
  version: string;
  active: boolean;
  isLatest: boolean;
  files: { file_name: string; tag: string; active: boolean }[];
  /** 该版本最后一次上传的人 */
  uploader: string;
  time: string;
  source: VersionSource;
  /** 中台同步的版本：对应的中台模型名称（中台记录名，去重） */
  ndpNames: string[];
  /** 该版本最后一次上传时的说明（中台同步的为中台记录说明） */
  description: string;
}

/** 一个模型类别的版本行（按时间倒序）；同一 tag 有被替换下来的旧行时优先展示 active 的那行。 */
export function buildVersionRows(name: string, history: VersionHistoryLike[]): VersionRow[] {
  const hist = [...history].sort((a, b) => b.latest_time.localeCompare(a.latest_time));
  const latestActive = hist.find((h) => h.active)?.version;
  return hist.map((h) => {
    const byTag = new Map<string, VersionFileLike>();
    for (const t of h.tags) {
      const prev = byTag.get(t.tag);
      if (!prev || (!prev.active && t.active) || (prev.active === t.active && t.create_time > prev.create_time)) {
        byTag.set(t.tag, t);
      }
    }
    const newest = [...h.tags].sort((a, b) => b.create_time.localeCompare(a.create_time))[0];
    const ndp = h.tags.some((t) => t.meta?.source === 'ndp');
    return {
      name,
      version: h.version,
      active: h.active,
      isLatest: h.version === latestActive,
      files: Array.from(byTag.values()).map((t) => ({ file_name: t.file_name, tag: t.tag, active: t.active })),
      uploader: newest?.uploader ?? '—',
      time: newest?.create_time ?? h.latest_time,
      source: ndp ? 'ndp' : 'upload',
      ndpNames: [...new Set(h.tags.map((t) => t.meta?.ndp_name).filter((n): n is string => typeof n === 'string' && n !== ''))],
      description: newest?.description?.trim() ?? '',
    };
  });
}

export type VersionStatusFilter = 'active' | 'inactive' | 'all';

export interface VersionFilter {
  version?: string;
  uploader?: string;
  /** 日期 YYYY-MM-DD，含两端 */
  from?: string;
  to?: string;
  status?: VersionStatusFilter;
  source?: VersionSource | '';
}

/** 是否设置了版本维度的筛选（有则命中的类别自动展开、隐藏未命中的类别）；状态只有「已停用」算筛选。 */
export function hasVersionFilter(f: VersionFilter): boolean {
  return Boolean(f.version?.trim() || f.uploader || f.from || f.to || f.source || f.status === 'inactive');
}

export function filterVersions(rows: VersionRow[], f: VersionFilter): VersionRow[] {
  const kw = f.version?.trim().toLowerCase() ?? '';
  const status = f.status ?? 'active';
  return rows.filter((r) => {
    if (status === 'active' && !r.active) {
      return false;
    }
    if (status === 'inactive' && r.active) {
      return false;
    }
    if (kw && !r.version.toLowerCase().includes(kw)) {
      return false;
    }
    if (f.uploader && r.uploader !== f.uploader) {
      return false;
    }
    const day = r.time.slice(0, 10);
    if (f.from && day < f.from) {
      return false;
    }
    if (f.to && day > f.to) {
      return false;
    }
    if (f.source && r.source !== f.source) {
      return false;
    }
    return true;
  });
}

/** 把文本按关键字（不区分大小写）切成片段，用于高亮。 */
export function splitHighlight(text: string, kw: string): { text: string; hit: boolean }[] {
  const k = kw.trim().toLowerCase();
  if (!k) {
    return [{ text, hit: false }];
  }
  const out: { text: string; hit: boolean }[] = [];
  const low = text.toLowerCase();
  let i = 0;
  while (i < text.length) {
    const j = low.indexOf(k, i);
    if (j < 0) {
      out.push({ text: text.slice(i), hit: false });
      break;
    }
    if (j > i) {
      out.push({ text: text.slice(i, j), hit: false });
    }
    out.push({ text: text.slice(j, j + k.length), hit: true });
    i = j + k.length;
  }
  return out;
}
